import React, {useEffect, useRef, useState} from "react";
import {
    useGetStorefrontProductCountQuery,
    useGetStorefrontsQuery
} from "../../store/endpoints/StorefrontSettingsEndpoints";
import SavedSearchStorefrontEntryDetails from "./SavedSearchStorefrontEntryDetails";
import {ISavedSearchStorefrontDetails} from "../../models/Search";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import MarkupTypeSelection from "../Storefront/MarkupTypeSelection";
import NumberField from "../Inputs/NumberField";
import {useGetOrganizationEntitlementsQuery} from "../../store/endpoints/OrganizationsEndpoints";

// Ordered from first-to-collapse to last-to-collapse
const COLLAPSIBLE_COLUMNS = ['minimumCost', 'minimumPrice', 'markupType', 'markup'] as const;
export type CollapsibleColumn = typeof COLLAPSIBLE_COLUMNS[number];

const COLUMN_BREAKPOINTS: Record<CollapsibleColumn, number> = {
    minimumCost: 1055,
    minimumPrice: 1027,
    markupType:   945,
    markup:       455,
};

function getHiddenColumns(width: number): CollapsibleColumn[] {
    return COLLAPSIBLE_COLUMNS.filter(col => width < COLUMN_BREAKPOINTS[col]);
}


interface ISavedSearchStorefrontSettings{
    savedSearchId: string;
    storefrontOverrideMarkup?: number;
    storefrontOverrideMarkupType?: string;
    minimumPrice?: number;
    minimumCost?: number;
    storefronts?: {[key: string]: ISavedSearchStorefrontDetails};
    includedStorefronts?: string[];
    onIncludedStorefrontsChange: (includedStorefronts: string[]) => void;
    onStorefrontsChange: (storefronts: {[key:string]: ISavedSearchStorefrontDetails}) => void;
    onStorefrontOverrideMarkupChange: (markup: number | undefined) => void;
    onStorefrontOverrideMarkupTypeChange: (markupType: string | undefined) => void;
    onStorefrontMinimumCostChange: (minimumCost: number | undefined) => void;
    onStorefrontMinimumPriceChange: (minimumPrice: number | undefined) => void;
    includeAllStorefronts?: boolean;
}
const SavedSearchStorefrontSettings: React.FC<ISavedSearchStorefrontSettings> = (props) => {
    const storefronts = useGetStorefrontsQuery();
    const [reachedTotalStorefrontLimit, setReachedTotalStorefrontLimit] = React.useState<boolean>(false);
    const org = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(org ?? '', {skip: !org || org === ''})
    const productCount = useGetStorefrontProductCountQuery();
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [hiddenColumns, setHiddenColumns] = useState<CollapsibleColumn[]>([]);

    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(entries => {
            const width = entries[0].contentRect.width;
            setHiddenColumns(getHiddenColumns(width));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        const limit = getOrgEntitlements.data?.storefrontProductLimit
        if (limit && productCount.data?.count != null) {
            setReachedTotalStorefrontLimit(productCount.data.count > limit);
        }
    }, [org, productCount.data]);
    
    const included = (storefrontId: string) => {
        if (props.includeAllStorefronts) {
            if(storefronts.data?.items?.find(a => a.id === storefrontId)?.ignoreInheritedProducts){
                return props.includedStorefronts?.includes(storefrontId)
            }
        }
        return props.includedStorefronts?.includes(storefrontId)
    }
    
    return (
        <div style={{paddingTop:'1em'}}>
            <h3>Default Storefront Settings</h3>
            {reachedTotalStorefrontLimit && <p>Product limit reached. Please remove products from storefronts to add more.</p>}
            <NumberField
                disabled={reachedTotalStorefrontLimit}
                label='Minimum Price'
                value={props.minimumPrice}
                onChange={props.onStorefrontMinimumPriceChange}
                decimal={true}
            />
            <NumberField
                disabled={reachedTotalStorefrontLimit}
                label='Minimum Cost'
                value={props.minimumCost}
                onChange={props.onStorefrontMinimumCostChange}
            />
            <NumberField
                disabled={reachedTotalStorefrontLimit}
                label='Override Markup'
                value={props.storefrontOverrideMarkup}
                onChange={props.onStorefrontOverrideMarkupChange}
                decimal={true}
            />
            <MarkupTypeSelection
                disabled={reachedTotalStorefrontLimit}
                label='Override Markup Type'
                value={props.storefrontOverrideMarkupType}
                onChange={props.onStorefrontOverrideMarkupTypeChange}
            />
            <br/>
            <div ref={tableContainerRef} style={{overflowX: 'auto' }}>
                <table className='data-table'>
                    <thead>
                    <tr>
                        <th></th>
                        <th>Storefront</th>
                        <th>Included</th>
                        {!hiddenColumns.includes('markup')      && <th>Markup</th>}
                        {!hiddenColumns.includes('markupType')  && <th>Markup Type</th>}
                        {!hiddenColumns.includes('minimumPrice') && <th>Minimum Price</th>}
                        {!hiddenColumns.includes('minimumCost') && <th>Minimum Cost</th>}
                        {hiddenColumns.length > 0               && <th></th>}
                    </tr>
                    </thead>
                    <tbody>
                    {storefronts.data?.items?.map((storefront) => {
                        if(!props.storefronts?.[storefront.id ?? '']){
                            return null;
                        }
                        return(
                            <SavedSearchStorefrontEntryDetails
                                savedSearchId={props.savedSearchId}
                                includeAllStorefronts={props.includeAllStorefronts}
                                reachedTotalStorefrontLimit={reachedTotalStorefrontLimit}
                                key={storefront.id}
                                storefrontId={storefront.id}
                                storefrontName={storefront.name}
                                included={included(storefront.id ?? '')}
                                onIncludedChange={(included) => props.onIncludedStorefrontsChange(included ? [...(props.includedStorefronts ?? []), storefront.id ?? ''] : (props.includedStorefronts ?? []).filter((id) => id !== storefront.id))}
                                storefrontEntry={props.storefronts?.[storefront.id ?? '']}
                                onStorefrontEntryChange={(storefrontEntry) => {
                                    props.onStorefrontsChange({
                                    ...props.storefronts,
                                    [storefront.id ?? '']: storefrontEntry})
                                }}
                                hiddenColumns={hiddenColumns}
                            />
                    )})}
                    </tbody>
                </table>
            </div>

        </div>)
}

export default SavedSearchStorefrontSettings;